import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons/faArrowUp';
import { faCircleStop } from '@fortawesome/pro-light-svg-icons/faCircleStop';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QChip, QIcon, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { agentIds } from '@captivateiq/assistant/config/agentConfig';
import type { AttachmentMeta } from '@captivateiq/assistant/types';
import {
  FEATURE_FLAG_AI_SHELL_FILE_UPLOAD,
  FEATURE_FLAG_AI_SHELL_MODEL_SWITCHER,
  isFeatureEnabled,
} from '@captivateiq/assistant/utils/featureFlags';
import { ModelSelector } from '@captivateiq/assistant/components/ModelSelector';

import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';

type MessageInputProps = {
  onSubmit: (message: string, attachments?: AttachmentMeta[]) => Promise<void>;
  agentThinking?: boolean;
};

const MAX_TEXTAREA_ROWS = 5;
const ACCEPTED_FILE_TYPES = '.csv,.pdf';
const MAX_ATTACHMENT_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB — must match backend MAX_ATTACHMENT_SIZE_BYTES

export const MessageInput = ({ onSubmit, agentThinking = false }: MessageInputProps) => {
  const { t } = useTranslation('assistant');
  const { isExpanded, createAttachment, selectedAgent, selectedModel, setSelectedModel } = useAIShellContext();
  const supportsAttachments =
    selectedAgent === agentIds.COMP_BUILDER_AGENT && isFeatureEnabled(FEATURE_FLAG_AI_SHELL_FILE_UPLOAD);
  const showModelSwitcher = isFeatureEnabled(FEATURE_FLAG_AI_SHELL_MODEL_SWITCHER);

  const [content, setContent] = useState<string>('');
  const [isMultiline, setIsMultiline] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<AttachmentMeta | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const singleLineHeightRef = useRef(0);

  // Capture the textarea's 1-line height once after mount so we can compare later.
  useLayoutEffect(() => {
    if (textRef.current) {
      singleLineHeightRef.current = textRef.current.offsetHeight;
    }
  }, []);

  // After each content change, check whether the textarea grew past its 1-line height.
  // We only switch TO multiline automatically; switching back to single-line only happens
  // when content is cleared (on submit or full deletion) to avoid layout oscillation at
  // the exact wrap boundary when the column mode gives the textarea extra width.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !singleLineHeightRef.current) return;
    if (el.offsetHeight > singleLineHeightRef.current) {
      setIsMultiline(true);
    }
  }, [content]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setContent(value);
    if (!value) setIsMultiline(false);
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Reset so the same file can be re-selected after dismissal
      const input = event.target as HTMLInputElement;
      input.value = '';

      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        window.DD_RUM?.addError('AI shell file rejected — too large', {
          size: file.size,
          limit: MAX_ATTACHMENT_SIZE_BYTES,
        });
        setAttachmentError(`File too large. Maximum size is ${MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)} MB.`);
        return;
      }

      setAttachmentError(null);
      setUploadInProgress(true);
      try {
        const meta = await createAttachment(file);
        setPendingAttachment(meta);
      } catch {
        setAttachmentError('Failed to upload file. Please try again.');
      } finally {
        setUploadInProgress(false);
      }
    },
    [createAttachment],
  );

  const handleDismissAttachment = useCallback(() => {
    setPendingAttachment(null);
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (agentThinking || content.trim() === '') {
        return;
      }
      const attachments = pendingAttachment ? [pendingAttachment] : undefined;
      setPendingAttachment(null);
      onSubmit(content, attachments);
      setContent('');
      setIsMultiline(false);
    },
    [onSubmit, setContent, content, agentThinking, pendingAttachment],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !agentThinking && content.trim() !== '') {
        event.preventDefault();
        handleSubmit(event);
      }
    },
    [handleSubmit, agentThinking, content],
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <QBox sx={expandedTextColumnSx(isExpanded)}>
      {showModelSwitcher && (
        <QBox sx={{ paddingBottom: '6px' }}>
          <ModelSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />
        </QBox>
      )}
      <form onSubmit={handleSubmit}>
        {attachmentError && (
          <QBox sx={{ paddingBottom: '4px', color: 'error.main', fontSize: '12px' }}>{attachmentError}</QBox>
        )}
        {pendingAttachment && (
          <QBox sx={{ paddingBottom: '4px' }}>
            <QChip
              deleteIcon={<QIcon icon={faTimes} sx={{ fontSize: '12px' }} />}
              label={pendingAttachment.fileName}
              size="small"
              onDelete={handleDismissAttachment}
            />
          </QBox>
        )}
        <QBox
          sx={{
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: isMultiline ? 'column' : 'row',
            alignItems: isMultiline ? 'stretch' : 'center',
            '&:focus-within': {
              borderColor: 'primary.main',
            },
          }}
        >
          {supportsAttachments && (
            <input
              accept={ACCEPTED_FILE_TYPES}
              data-testid="file-upload-input"
              ref={fileInputRef}
              style={{ display: 'none' }}
              type="file"
              onChange={handleFileChange}
            />
          )}
          <QTextField
            data-testid="ai-message-input"
            inputRef={textRef}
            maxRows={MAX_TEXTAREA_ROWS}
            multiline
            optional
            placeholder={t('input_placeholders.message') as string}
            sx={{
              ...(isMultiline ? {} : { flex: 1 }),
              padding: 0,
              '& .MuiInputBase-root': { border: 'none' },
              '& .MuiInputBase-root.Mui-focused': { outline: 'none' },
            }}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <QBox
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              padding: isMultiline ? '0 6px 6px' : '6px',
              flexShrink: 0,
            }}
          >
            {supportsAttachments && (
              <QButton
                data-testid="ai-attach-button"
                disabled={uploadInProgress || agentThinking}
                sx={{
                  minWidth: 'unset',
                  padding: '4px',
                  borderRadius: '4px',
                  height: '24px',
                  width: '24px',
                }}
                type="button"
                variant="text"
                onClick={handleAttachClick}
              >
                <QIcon icon={faPaperclip} sx={{ width: '10.9px', height: '12.5px' }} />
              </QButton>
            )}
            <QButton
              data-testid="ai-send-button"
              size="small"
              sx={{
                minWidth: 'unset',
                padding: '4px 4px 4px 4px',
                borderRadius: '4px',
                height: '24px',
                width: '24px',
              }}
              type="submit"
              variant={content && !agentThinking ? 'contained' : 'outlined'}
            >
              <QIcon icon={agentThinking ? faCircleStop : faArrowUp} sx={{ width: '9.6px', height: '11.2px' }} />
            </QButton>
          </QBox>
        </QBox>
      </form>
    </QBox>
  );
};
